import mapValues from 'lodash-es/mapValues';
import every from 'lodash-es/every';

type Point = {
  x: number,
  y: number
};

type Rect = {
  top: number,
  left: number,
  width: number,
  height: number
};

type Corners = {
  topLeft: Point,
  topRight: Point,
  bottomLeft: Point,
  bottomRight: Point
};

function getVisibleDimension(position: number, dimension: number, screenDimension: number): number {
  let visibleDimension: number;
  if (position < 0) {
    visibleDimension = dimension + position;
  } else if (position + dimension > screenDimension) {
    visibleDimension = screenDimension - position;
  } else {
    visibleDimension = dimension;
  }

  if (visibleDimension < 0) {
    return 0;
  }

  if (visibleDimension > dimension) {
    return dimension;
  }

  return visibleDimension;
}

function getVisibility(position: number, dimension: number, screenDimension: number): number {
  if (dimension === 0) return 0;

  return getVisibleDimension(position, dimension, screenDimension) / dimension;
}

function getRectArea(rect: Rect): number {
  return rect.height * rect.width;
}

export function getVerticalVisibility(element: Element, viewPortPadding: number = 0): number {
  const { top, height } = element.getBoundingClientRect();
  const { clientHeight } = document.body || {};

  return getVisibility(top - viewPortPadding, height, clientHeight - (viewPortPadding * 2));
}

function getHorizontalVisibility(element: Element, viewPortPadding: number = 0): number {
  const { left, width } = element.getBoundingClientRect();
  const { clientWidth } = document.body || {};

  return getVisibility(left - viewPortPadding, width, clientWidth - (viewPortPadding * 2));
}

function getElementVisibility(element: Element, viewPortPadding: number = 0): number {
  const verticalVisibility = getVerticalVisibility(element, viewPortPadding);
  const horizontalVisibility = getHorizontalVisibility(element, viewPortPadding);

  return verticalVisibility * horizontalVisibility;
}

export function isFullyVisible(element: Element, viewPortPadding: number = 0): boolean {
  return getElementVisibility(element, viewPortPadding) === 1;
}

function isFullyHidden(element: Element): boolean {
  return getElementVisibility(element) === 0;
}

function getVisibleRect(element: Element): Rect {
  const {
    top, height,
    left, width,
  } = element.getBoundingClientRect();
  const { clientHeight, clientWidth } = document.body || {};

  const visibleHeight = getVisibleDimension(top, height, clientHeight);
  const visibleWidth = getVisibleDimension(left, width, clientWidth);

  const visibleRect: Rect = {
    top: 0,
    left: 0,
    height: visibleHeight,
    width: visibleWidth,
  };
  visibleRect.top = top < 0 ? 0 : top;

  visibleRect.left = left < 0 ? 0 : left;

  return visibleRect;
}

function getElementFromPoint(point: Point) {
  return document.elementFromPoint(point.x, point.y);
}

function getCorners(rect: Rect, pixelsInside: number = 0): Corners {
  const left = rect.left + pixelsInside;
  const top = rect.top + pixelsInside;
  const right = rect.left + rect.width - pixelsInside;
  const bottom = rect.top + rect.height - pixelsInside;

  const topLeft = {
    x: left,
    y: top,
  } as const;
  const topRight = {
    x: right,
    y: top,
  } as const;
  const bottomLeft = {
    x: left,
    y: bottom,
  } as const;
  const bottomRight = {
    x: right,
    y: bottom,
  } as const;

  return {
    topLeft,
    topRight,
    bottomLeft,
    bottomRight,
  };
}

export function isElementPartiallyVisibleAndOnTop(element: Element) {
  if (isFullyHidden(element)) return false;

  const visibleRect = getVisibleRect(element);
  const corners = getCorners(visibleRect, 10);
  const elementsInCorners = mapValues(corners, (corner) => getElementFromPoint(corner));

  return every(
    elementsInCorners,
    (elementInCorner) => element.contains(elementInCorner),
  );
}

export function getElementVisibleArea(element: Element): number {
  return getRectArea(getVisibleRect(element));
}

export function getElementTopLeftPosition(element: Element): number {
  const { top, left } = element.getBoundingClientRect();
  const { clientWidth } = document.body || {};

  return (top * clientWidth) + left;
}
